import React, { useState, useEffect, useCallback } from 'react';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import AttendancePanel from './components/AttendancePanel';
import MemberRoster from './components/MemberRoster';
import WorkoutPlanBuilder from './components/WorkoutPlanBuilder';
import DietPlanManager from './components/DietPlanManager';
import TrainerStats from './components/TrainerStats';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function StaffTrainerInterface() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workout');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [shiftStats, setShiftStats] = useState([
    { label: 'Checked In Today', value: '—', icon: 'UserCheck', color: 'var(--color-success)' },
    { label: 'Active Sessions', value: '—', icon: 'Activity', color: 'var(--color-primary)' },
    { label: 'Pending Check-Outs', value: '—', icon: 'Clock', color: 'var(--color-warning)' },
    { label: 'Trainer Assignments', value: '—', icon: 'Users', color: 'var(--color-secondary)' },
  ]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const fetchShiftStats = useCallback(async () => {
    try {
      const { data } = await api.get('/attendances/today');
      const attendances = data || [];
      const checkedInToday = attendances?.length;
      const activeSessions = attendances?.filter(a => !a?.check_out_time)?.length;
      setShiftStats([
        { label: 'Checked In Today', value: String(checkedInToday), icon: 'UserCheck', color: 'var(--color-success)' },
        { label: 'Active Sessions', value: String(activeSessions), icon: 'Activity', color: 'var(--color-primary)' },
        { label: 'Pending Check-Outs', value: String(activeSessions), icon: 'Clock', color: 'var(--color-warning)' },
        { label: 'Trainer Assignments', value: '—', icon: 'Users', color: 'var(--color-secondary)' },
      ]);
    } catch (err) {
      console.error('Failed to fetch shift stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchShiftStats();
  }, [fetchShiftStats]);

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Staff & Trainer Interface</h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Manage attendance, training programs, and member progress</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)]">
              <span className="w-2 h-2 rounded-full bg-[var(--color-success)] animate-pulse"></span>
              <span className="text-sm font-medium text-[var(--color-success)] font-[var(--font-caption)]">Shift Active</span>
            </div>
            <div className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              {new Date()?.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            <button
              onClick={() => navigate('/account-settings')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] text-[var(--color-muted-foreground)] rounded-[var(--radius-sm)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
            >
              <Icon name="Settings" size={13} color="currentColor" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-[var(--radius-sm)] hover:bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/50 transition-colors"
            >
              <Icon name="LogOut" size={13} color="var(--color-error)" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Shift Stats Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {shiftStats?.map((stat) => (
            <div key={stat?.label} className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)] flex items-center gap-3">
              <div className="w-10 h-10 rounded-[var(--radius-sm)] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${stat?.color}18` }}>
                <Icon name={stat?.icon} size={20} color={stat?.color} strokeWidth={2} />
              </div>
              <div>
                <p className="text-xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)] leading-none">{stat?.value}</p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{stat?.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Layout: Attendance + Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 mb-6">
          {/* Left: Attendance Panel */}
          <AttendancePanel />

          {/* Right: Tabbed Content */}
          <div className="flex flex-col gap-4">
            {/* Tab Navigation */}
            <div className="flex gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-1 shadow-[var(--shadow-sm)]">
              {[
                { id: 'workout', label: 'Workout Plans', icon: 'Dumbbell' },
                { id: 'diet', label: 'Diet Plans', icon: 'UtensilsCrossed' },
                { id: 'roster', label: 'Member Roster', icon: 'Users' },
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded text-sm font-medium font-[var(--font-caption)] transition-all duration-200 ${
                    activeTab === tab?.id ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                  }`}
                >
                  <Icon name={tab?.icon} size={15} color={activeTab === tab?.id ? '#fff' : 'currentColor'} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'workout' && <WorkoutPlanBuilder />}
            {activeTab === 'diet' && <DietPlanManager />}
            {activeTab === 'roster' && <MemberRoster />}
          </div>
        </div>

        {/* Member Roster (always visible below on large screens) */}
        <div className="hidden lg:block mb-6">
          {activeTab !== 'roster' && <MemberRoster />}
        </div>

        {/* Trainer Stats */}
        <TrainerStats />
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loggingOut && setShowLogoutModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="LogOut" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Log Out?</h2>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Are you sure you want to log out?
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  disabled={loggingOut}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {loggingOut ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Logging out...
                    </>
                  ) : (
                    <>
                      <Icon name="LogOut" size={14} color="#fff" />Log Out
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
